import { useId, useRef, useState } from 'react';
import { ArrowDownToLine, ChevronDown, ChevronUp, LoaderCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useI18n } from '../i18n/i18n-provider';
import { usePwa } from './pwa-provider';
import './pwa-status.css';

const copy = {
  cs: {
    label: 'Stav aplikace', open: 'Otevřít stav aplikace', close: 'Sbalit stav aplikace', later: 'Později',
    offlineTitle: 'Bez připojení', offline: 'Nové záznamy nejsou potvrzenou prezencí, dokud je neověří server. Čekající záznamy zkontrolujete v offline frontě skeneru.',
    updateTitle: 'Nová verze', update: 'Je připravena nová verze Eventu. Načtete ji, až vám to bude vyhovovat.',
    blocked: 'Nejprve dokončete rozpracovanou práci a odešlete čekající záznamy. Během práce s kamerou se aktualizace neprovádí.',
    activate: 'Připravit aktualizaci', ready: 'Nová verze je připravena k načtení.', reload: 'Načíst novou verzi',
    readyTitle: 'Připraveno k načtení', preparingTitle: 'Ověřuji aktualizaci', preparing: 'Ověřuji bezpečnou aktualizaci…',
    coordinating: 'Krátce ověřujeme bezpečný okamžik pro aktualizaci všech oken. Žádné okno se samo neobnoví.',
    busyClients: 'V jiném okně právě probíhá práce. Až bude dokončená a záznamy odeslané, můžete přípravu zkusit znovu. Žádné okno se neobnoví samo.',
    unverifiedClients: 'Čekáme na ověření bezpečného okamžiku ve všech oknech Eventu. Pokračujte v práci a přípravu zkuste později. Vaše okna zůstávají otevřená.',
    failed: 'Bezpečné podmínky aktualizace se nepodařilo ověřit. Nic se neobnovuje automaticky; zkuste to znovu.',
    installTitle: 'Aplikace na plochu', install: 'Nainstalovat Event', ios: 'Jak přidat na plochu',
    installDescription: 'Otevírejte Event pohodlně přímo z plochy. Instalace sama nepotvrzuje dostupnost prezence bez připojení.',
    installError: 'Instalaci se nepodařilo otevřít. Event můžete dál používat v prohlížeči a zkusit to později.',
    iosSteps: 'V Safari otevřete Sdílet → Přidat na plochu. Potom potvrďte Přidat.',
  },
  en: {
    label: 'Application status', open: 'Open application status', close: 'Collapse application status', later: 'Later',
    offlineTitle: 'Offline', offline: 'New records are not confirmed attendance until the server verifies them. Check pending records in the scanner’s offline queue.',
    updateTitle: 'New version', update: 'A new version of Event is available. Load it when it suits you.',
    blocked: 'First finish your work and send pending records. Updates are paused while you are using the camera.',
    activate: 'Prepare update', ready: 'The new version is ready to load.', reload: 'Load new version',
    readyTitle: 'Ready to load', preparingTitle: 'Checking update', preparing: 'Checking update safety…',
    coordinating: 'Briefly checking a safe moment for updating all windows. No window reloads on its own.',
    busyClients: 'Work is in progress in another window. Once it is finished and records are sent, try preparing again. No window reloads on its own.',
    unverifiedClients: 'Waiting to verify a safe moment across all Event windows. Keep working and try preparing later. Your windows stay open.',
    failed: 'Safe update conditions could not be verified. Nothing reloads automatically; please try again.',
    installTitle: 'App on your Home Screen', install: 'Install Event', ios: 'How to add to Home Screen',
    installDescription: 'Open Event directly from your Home Screen. Installing the app does not confirm offline attendance readiness.',
    installError: 'The installation prompt could not be opened. You can keep using Event in your browser and try later.',
    iosSteps: 'In Safari, open Share → Add to Home Screen. Then confirm Add.',
  },
} as const;

export function PwaStatus() {
  const { locale } = useI18n();
  const pwa = usePwa();
  const text = copy[locale];
  const panelId = useId();
  const chipRef = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [installError, setInstallError] = useState(false);
  const hasUpdate = pwa.updateAvailable || pwa.updateActivated;
  const failure: string | undefined = pwa.updateFailure;
  const failureText = failure === 'busy_clients' ? text.busyClients
    : failure === 'clients_unverified' || failure === 'other_windows' ? text.unverifiedClients
      : failure ? text.failed : undefined;
  const title = pwa.updatePreparing || pwa.updateCoordinating ? text.preparingTitle
    : pwa.updateActivated ? text.readyTitle : pwa.updateAvailable ? text.updateTitle
      : pwa.offline ? text.offlineTitle : text.installTitle;
  const Icon = pwa.updatePreparing || pwa.updateCoordinating ? LoaderCircle : hasUpdate ? RefreshCw : pwa.offline ? WifiOff : ArrowDownToLine;
  const updateMessage = pwa.updatePreparing ? text.preparing : pwa.updateActivated ? text.ready : text.update;
  // The live text depends only on application state, not opening or dismissing the panel.
  const announcement = [pwa.offline ? `${text.offlineTitle}. ${text.offline}` : '',
    hasUpdate ? updateMessage : '', pwa.updateCoordinating ? text.coordinating : '',
    hasUpdate && pwa.updateBlocked && !pwa.updateCoordinating ? text.blocked : '', failureText ?? '',
    installError ? text.installError : ''].filter(Boolean).join(' ');
  if (!pwa.offline && !hasUpdate && !pwa.updateCoordinating && !pwa.canInstall && !pwa.iosInstallHint) return null;
  return <>
    <span className="pwa-status__announcer" role="status" aria-live="polite" aria-atomic="true">{announcement}</span>
    <aside className={`pwa-status${expanded ? ' pwa-status--expanded' : ''}`} aria-label={text.label}>
      <button ref={chipRef} type="button" className="pwa-status__chip" aria-label={`${expanded ? text.close : text.open}: ${title}`}
        aria-expanded={expanded} aria-controls={panelId} onClick={() => setExpanded((value) => !value)}>
        <Icon size={18} aria-hidden="true" className={pwa.updatePreparing || pwa.updateCoordinating ? 'pwa-status__spinner' : undefined} />
        <span>{title}</span>
        {pwa.offline && hasUpdate && <span className="pwa-status__connectivity"><WifiOff size={15} aria-hidden="true" />{text.offlineTitle}</span>}
        {expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronUp size={16} aria-hidden="true" />}
      </button>
      <div id={panelId} className="pwa-status__panel" hidden={!expanded}>
        {pwa.updateCoordinating && <p className="pwa-status__explanation">{text.coordinating}</p>}
        {hasUpdate && <div className="pwa-status__update">
          <p className="pwa-status__heading">{updateMessage}</p>
          {pwa.updateBlocked && !pwa.updateCoordinating && <p className="pwa-status__explanation">{text.blocked}</p>}
          {failureText && <p className="pwa-status__explanation pwa-status__notice">{failureText}</p>}
          <button type="button" className="cta cta--primary pwa-status__action" disabled={pwa.updateBlocked || pwa.updatePreparing || pwa.updateCoordinating}
            onClick={pwa.updateActivated ? pwa.reloadUpdatedApp : pwa.activateUpdate}>
            <RefreshCw size={17} aria-hidden="true" />
            {pwa.updatePreparing ? text.preparing : pwa.updateActivated ? text.reload : text.activate}
          </button>
        </div>}
        {pwa.offline && <div className="pwa-status__offline"><p className="pwa-status__heading"><WifiOff size={16} aria-hidden="true" />{text.offlineTitle}</p><p>{text.offline}</p></div>}
        {(pwa.canInstall || pwa.iosInstallHint) && <div className="pwa-status__install">
          <p className="pwa-status__explanation">{text.installDescription}</p>
          {pwa.canInstall && <button type="button" className="cta cta--secondary pwa-status__action" onClick={() => {
            setInstallError(false);
            void pwa.requestInstall().catch(() => setInstallError(true));
          }}><ArrowDownToLine size={17} aria-hidden="true" />{text.install}</button>}
          {pwa.iosInstallHint && <details><summary>{text.ios}</summary><p>{text.iosSteps}</p></details>}
          {installError && <p className="pwa-status__notice">{text.installError}</p>}
        </div>}
        <button type="button" className="pwa-status__later" onClick={() => {
          setExpanded(false);
          chipRef.current?.focus();
        }}>{text.later}</button>
      </div>
    </aside>
  </>;
}
